import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import type { RuntimeConfig } from '@/lib/runtime-config';
import { formatDateTime, formatRelativeTime } from '@/lib/format';
import { strings } from '@/lib/strings';
import { getRepoStatus, listRepos, type RepoStatus, type RepoSummary, WebApiError } from '@/lib/web-api';

interface RepoListPageProps {
  runtimeConfig: RuntimeConfig;
  tenantId: string;
}

type StatusState = {
  label: string;
  variant: 'default' | 'success' | 'warning' | 'error' | 'accent';
};

const mapStatus = (status?: RepoStatus): StatusState => {
  const state = status?.state?.toUpperCase();
  switch (state) {
    case 'IDLE':
      return { label: strings.syncState.idle, variant: 'success' };
    case 'SYNCING':
      return { label: strings.syncState.syncing, variant: 'accent' };
    case 'ERROR':
      return { label: strings.syncState.error, variant: 'error' };
    case 'OFFLINE':
      return { label: strings.syncState.offline, variant: 'warning' };
    default:
      return { label: strings.syncState.unknown, variant: 'default' };
  }
};

export const RepoListPage = ({ runtimeConfig, tenantId }: RepoListPageProps) => {
  const [repos, setRepos] = useState<RepoSummary[]>([]);
  const [statusMap, setStatusMap] = useState<Record<string, RepoStatus>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const fetchedRepos = await listRepos(runtimeConfig, tenantId);
        if (cancelled) {
          return;
        }

        setRepos(fetchedRepos);

        const statusResults = await Promise.allSettled(
          fetchedRepos.map(repo => getRepoStatus(runtimeConfig, tenantId, repo.repo_id))
        );

        if (cancelled) {
          return;
        }

        const nextMap: Record<string, RepoStatus> = {};
        statusResults.forEach((result, index) => {
          if (result.status === 'fulfilled') {
            nextMap[fetchedRepos[index].repo_id] = result.value;
          }
        });
        setStatusMap(nextMap);
      } catch (err) {
        if (cancelled) {
          return;
        }
        if (err instanceof WebApiError) {
          setError(err.message);
        } else {
          setError('Unable to load repositories.');
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [runtimeConfig, tenantId, refreshToken]);

  const totalRepos = repos.length;
  const lastUpdated = useMemo(() => {
    const timestamps = Object.values(statusMap)
      .map(status => status.last_sync_unix_ms)
      .filter((value): value is number => typeof value === 'number');
    if (!timestamps.length) {
      return null;
    }
    return Math.max(...timestamps);
  }, [statusMap]);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Repository overview</CardTitle>
          <CardDescription>Track sync health across your Filesync repos.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-3xl font-semibold">{isLoading ? '—' : totalRepos}</p>
            <p className="text-sm text-muted-foreground">Repositories connected</p>
          </div>
          <div className="text-sm text-muted-foreground">
            <p className="font-medium text-foreground">Last sync activity</p>
            <p>{lastUpdated ? formatRelativeTime(lastUpdated) : 'No sync history yet'}</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setRefreshToken(value => value + 1);
            }}
          >
            Refresh
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Repositories</CardTitle>
          <CardDescription>Open a repo to browse snapshots and files.</CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          ) : error ? (
            <div className="rounded-lg border border-error/30 bg-error/10 p-4 text-sm text-error">
              {error}
            </div>
          ) : repos.length === 0 ? (
            <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
              No repositories yet. Create one in the control plane to get started.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Repository</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last sync</TableHead>
                  <TableHead className="text-right">Open</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {repos.map(repo => {
                  const status = statusMap[repo.repo_id];
                  const badge = mapStatus(status);
                  const createdAt = repo.created_at ? new Date(repo.created_at).getTime() : undefined;
                  return (
                    <TableRow key={repo.repo_id}>
                      <TableCell>
                        <div>
                          <p className="font-medium text-foreground">{repo.repo_name}</p>
                          <p className="text-xs text-muted-foreground">{repo.repo_id}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm">{createdAt ? formatRelativeTime(createdAt) : '—'}</p>
                          <p className="text-xs text-muted-foreground">{formatDateTime(createdAt)}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={badge.variant}>{badge.label}</Badge>
                      </TableCell>
                      <TableCell>
                        {status?.last_sync_unix_ms
                          ? formatRelativeTime(status.last_sync_unix_ms)
                          : strings.syncState.unknown}
                      </TableCell>
                      <TableCell className="text-right">
                        <Link
                          to={`/repos/${repo.repo_id}`}
                          className="text-sm font-medium text-primary hover:underline"
                        >
                          Browse
                        </Link>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
